import { Icon } from '@/shared/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './search.css';
import {
  SEARCH_KINDS,
  SEARCH_SEE_ALL,
  searchRecords,
  getRecents,
  pushRecent,
  type SearchKind,
  type SearchRecord,
} from './searchIndex';

const PER_GROUP = 5;

/** Wrap every case-insensitive occurrence of `q` in <mark> (bg #fdf0cf). */
function Highlight({ text, q }: { text: string; q: string }) {
  const needle = q.trim();
  if (needle === '') return <>{text}</>;
  const lower = text.toLowerCase();
  const nl = needle.toLowerCase();
  const parts: React.ReactNode[] = [];
  let i = 0;
  let k = 0;
  for (;;) {
    const j = lower.indexOf(nl, i);
    if (j < 0) {
      parts.push(text.slice(i));
      break;
    }
    if (j > i) parts.push(text.slice(i, j));
    parts.push(<mark key={k++}>{text.slice(j, j + needle.length)}</mark>);
    i = j + needle.length;
  }
  return <>{parts}</>;
}

/** True when focus is in a field, so "/" types a slash instead of opening. */
function typingInField(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (el as HTMLElement).isContentEditable;
}

/**
 * One search for everything (rcm-27, 00-ds-09 §5, D148): a field at the top of
 * the sidebar plus ⌘K / Ctrl+K or "/" opens a palette that searches patients,
 * claims, payments, documents, appeals, pre-auths and checks & deposits — 5 per
 * kind with a count and "See all". Empty query shows the last records opened.
 * UI-only over a mock index; production is a server search (§8).
 */
export function GlobalSearch({ collapsed = false }: { collapsed?: boolean }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === '/' && !open && !typingInField()) {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => { setActive(0); }, [query]);

  const empty = query.trim() === '';
  // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh the list each time the palette opens
  const recents = useMemo(() => getRecents(), [open]);

  const groups = useMemo(() => {
    if (empty) return [];
    const results = searchRecords(query);
    return SEARCH_KINDS.map((kind) => ({ kind, rows: results.filter((r) => r.kind === kind) })).filter(
      (g) => g.rows.length > 0,
    );
  }, [empty, query]);

  // The keyboard-navigable flat list, in render order.
  const flat: SearchRecord[] = empty ? recents : groups.flatMap((g) => g.rows.slice(0, PER_GROUP));

  const go = (rec: SearchRecord) => {
    pushRecent(rec);
    setOpen(false);
    void navigate(rec.to);
  };
  const seeAll = (kind: SearchKind) => {
    const base = SEARCH_SEE_ALL[kind];
    setOpen(false);
    void navigate(query.trim() ? `${base}?q=${encodeURIComponent(query.trim())}` : base);
  };

  const onInputKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(flat.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const rec = flat[active];
      if (rec) go(rec);
    }
  };

  const activeId = flat[active]?.id;

  const renderRow = (r: SearchRecord) => (
    <button
      key={r.id}
      type="button"
      className={`gs-row${r.id === activeId ? ' on' : ''}`}
      onMouseEnter={() => { setActive(flat.findIndex((x) => x.id === r.id)); }}
      onClick={() => { go(r); }}
    >
      <div className="gs-row-main">
        <div className={`gs-row-title${r.titleMono ? ' mono' : ''}`}>
          <Highlight text={r.title} q={query} />
        </div>
        <div className="gs-row-sub"><Highlight text={r.sub} q={query} /></div>
      </div>
      {r.right ? <div className="gs-row-right">{r.right}</div> : null}
    </button>
  );

  return (
    <>
      <button
        type="button"
        className={`gs-trigger${collapsed ? ' collapsed' : ''}`}
        aria-label="Search everything"
        onClick={() => { setOpen(true); }}
      >
        <Icon kind="search" size={16} aria-hidden />
        {collapsed ? null : (
          <>
            <span className="gs-trigger-label">Search</span>
            <span className="gs-kbd" aria-hidden>⌘K</span>
          </>
        )}
      </button>

      {open ? (
        <div
          className="gs-backdrop"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className="gs-panel" role="dialog" aria-modal="true" aria-label="Search">
            <div className="gs-field">
              <Icon kind="search" size={18} aria-hidden />
              <input
                ref={inputRef}
                type="search"
                value={query}
                placeholder="Search"
                aria-label="Search everything"
                onChange={(e) => { setQuery(e.target.value); }}
                onKeyDown={onInputKey}
              />
              <span className="gs-kbd" aria-hidden>esc</span>
            </div>

            <div className="gs-results">
              {empty ? (
                recents.length > 0 ? (
                  <div>
                    <div className="gs-group-label"><span>Recent</span></div>
                    {recents.map(renderRow)}
                  </div>
                ) : (
                  <div className="gs-hint">Search patients, claims, payments, documents, appeals, pre-auths and checks.</div>
                )
              ) : groups.length === 0 ? (
                <div className="gs-empty">Nothing matches “{query.trim()}”.</div>
              ) : (
                groups.map((g) => (
                  <div key={g.kind}>
                    <div className="gs-group-label">
                      <span>{g.kind}</span>
                      <span className="gs-group-count">{g.rows.length}</span>
                      <button type="button" className="gs-seeall" onClick={() => { seeAll(g.kind); }}>
                        See all
                      </button>
                    </div>
                    {g.rows.slice(0, PER_GROUP).map(renderRow)}
                  </div>
                ))
              )}
            </div>

            <div className="gs-footer">
              <span><kbd>↑↓</kbd> to move · <kbd>↵</kbd> to open</span>
              <span>Searches names, dates of birth, member IDs, claim, check and trace numbers, and amounts</span>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
